import { usePropertyBuilder } from "@/components/views/config/helpers";
import { type ModTag } from "@/components/views/mods";
import { useSteamWorkshop } from "@/components/views/mods/steam_workshop";
import type { PalworldProfileNamespace, SteamWorkshopItem } from "@/graphql";
import { useServiceStore } from "@/store/service";
import { useRouteUtils } from "@/util/route";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBox,
  faClockRotateLeft,
  faCode,
  faDatabase,
  faDiagramProject,
  faFileImport,
  faPuzzlePiece,
  faScroll,
} from "@fortawesome/pro-solid-svg-icons";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { commonMenuItems } from "./common";
import defaultProfile, { asyncRoute } from "./default";
import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";

// These commands have corresponding localisation keys for their description and arguments
const consoleCommands = ["Shutdown", "Broadcast", "KickPlayer", "BanPlayer", "ShowPlayers", "Info", "Save"];

// virtual.mods stores source-prefixed repo keys (TR-5156). Palworld has one mod tab per source, each
// owning only its own prefix — entries of other sources pass through its operations untouched. The
// shared Mods.vue page and this composable resolve the active source from the tab route.
interface ModSourceConfig {
  prefix: string;
  storeTitleKey: string;
  // requiresClientMod carries no signal for Steam Workshop items: every Info.json ships client-side
  // install rules, so the ingestion heuristic flags ALL of them (357/357 in prod) and the tab-level
  // banner covers it. Direct-placement sources (Nexus/CurseForge) derive the flag per mod, so the
  // per-mod badge/hint stays meaningful for them.
  perModClientFlag: boolean;
}

const DEFAULT_MOD_SOURCE_ROUTE = "steam-workshop";
const MOD_SOURCE_CONFIGS: Record<string, ModSourceConfig> = {
  [DEFAULT_MOD_SOURCE_ROUTE]: {
    prefix: "mod.steam.workshop.",
    storeTitleKey: "ngp/mods/dialog/link/steamworkshop",
    perModClientFlag: false,
  },
  "nexus-mods": {
    prefix: "modulus_nexusmods_palworld_",
    storeTitleKey: "ngp/mods/dialog/link/nexusmods",
    perModClientFlag: true,
  },
  "curseforge-mods": {
    prefix: "modulus_curseforge_palworld_",
    storeTitleKey: "ngp/mods/dialog/link/curseforge",
    perModClientFlag: true,
  },
};

// Mod functional type → tag. The names are proper nouns (not translated); the icon is decorative.
// `mod_type` is a source-agnostic column on game_palworld_mods, classified at ingestion (TR-5159).
const MOD_TYPE_TAGS: Record<string, { label: string; icon: IconDefinition }> = {
  lua: { label: "Lua", icon: faScroll },
  blueprint: { label: "Blueprint", icon: faDiagramProject },
  cpp: { label: "C++", icon: faCode },
  palschema: { label: "PalSchema", icon: faDatabase },
  pak: { label: "Pak", icon: faBox },
};

// Direct-placement mods have no load order, so the manage page shows no reorder arrows
// (allowModsReordering is off) — but it is still where installed mods are removed.
const modsMenuItem = (route: string) =>
  new MenuItem(
    route,
    faPuzzlePiece,
    asyncRoute(() => import("../components/views/config/palworld/Mods.vue")),
    [
      {
        route: "manage",
        component: asyncRoute(() => import("../components/views/mods/manage/ModsManage.vue")),
      },
    ],
  );

// Same route/component/icon as the shared Logs item, but labelled "UE4SS-Logs" – it only surfaces
// UE4SS mod output. Route stays "logs" so the logs-tab behaviour keyed on it (headers, info box)
// keeps working.
const ue4ssLogsMenuItem = new MenuItem(
  commonMenuItems.logs.route,
  commonMenuItems.logs.icon,
  commonMenuItems.logs.component,
  commonMenuItems.logs.subRoutes,
  "ngp/route/ue4ss-logs",
);

const createMenu = (ue4ssEnabled: boolean, modsEnabled: boolean): Menu => {
  const menu = defaultProfile().menu;
  // menu[0].items = [status, settings, fileManager]. Save Backups (TR-5177) sits directly below
  // File Manager. UE4SS-Logs goes below Console and is hidden unless UE4SS is enabled – otherwise
  // the tab is empty and confuses customers.
  menu[0].items.push(
    new MenuItem(
      "save-backups",
      faClockRotateLeft,
      asyncRoute(() => import("../components/views/palworld/LevelSavBackups.vue")),
    ),
    // Save Import (TR-5175): upload a world save from a PC or another host and apply it.
    new MenuItem(
      "save-import",
      faFileImport,
      asyncRoute(() => import("../components/views/palworld/SaveImport.vue")),
    ),
    commonMenuItems.console,
  );
  if (ue4ssEnabled) {
    menu[0].items.push(ue4ssLogsMenuItem);
  }
  // The three mod-source tabs get their own sidebar section between Settings and Administration.
  // PC only: the mods (UE4SS/pak, from Steam Workshop/Nexus/CurseForge) load on the Steam dedicated
  // server binary; the console / Microsoft-Store variants can't run them (see default export).
  if (modsEnabled) {
    menu.splice(1, 0, { name: "modding", items: Object.keys(MOD_SOURCE_CONFIGS).map(modsMenuItem) });
  }
  return menu;
};

export function useMods() {
  const { t } = useI18n();
  const service = useServiceStore();
  const { buildProperty } = usePropertyBuilder();
  const { tabSegments } = useRouteUtils();

  // The Mods.vue page is shared across the source tabs; the active tab route selects which source
  // this composable operates on. The shared mod components (DialogManageMod/ModTags/ModDrag) resolve
  // useMods() during their own setup, always within the active tab, so the route is authoritative.
  const sourceConfig = MOD_SOURCE_CONFIGS[tabSegments.value[0]] ?? MOD_SOURCE_CONFIGS[DEFAULT_MOD_SOURCE_ROUTE];

  const profile = computed(() => service.ctx.profile as PalworldProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const subscribedFileIds = buildProperty<Array<string>>("virtual", "mods", []);
  const collectionsStub = computed({
    get() {
      return [];
    },
    set() {},
  });
  const workshop = useSteamWorkshop(
    selectedWorkshopItems,
    subscribedFileIds,
    collectionsStub,
    "",
    false,
    false,
    sourceConfig.prefix,
  );

  const storeTitle = computed(() => t(sourceConfig.storeTitleKey));

  // isServer is tri-state: true = server-compatible, false = client-only, null = metadata not
  // ingested yet (item not downloaded). Only true is selectable — the backend mutator drops
  // everything else from targetPackages on save anyway (TR-5156), so make that visible up front.
  const isModAddable = (mod: SteamWorkshopItem) => mod.isServer === true;

  const showClientModFlag = (mod: SteamWorkshopItem) => sourceConfig.perModClientFlag && mod.requiresClientMod;

  const modTags = (mod: SteamWorkshopItem): ModTag[] => {
    const tags: ModTag[] = [];
    // Mod type (Lua / Blueprint / C++ / PalSchema / Pak) — proper names, no translation needed.
    const typeTag = mod.modType ? MOD_TYPE_TAGS[mod.modType] : undefined;
    if (typeTag) {
      tags.push(typeTag);
    }
    // Loader hint: this mod needs the UE4SS runtime enabled.
    if (mod.requiresUe4ss) {
      tags.push({ label: t("ngp/palworld/mods/tag/requires_ue4ss"), icon: faPuzzlePiece });
    }
    // No "Server mod" tag: everything the browse view lists is already server-installable
    // (it filters on is_server). The client-only / pending states only surface in the installed
    // list, where a mod's metadata may have changed after selection.
    if (mod.isServer === false) {
      tags.push(t("ngp/palworld/mods/tag/client_only"));
    } else if (mod.isServer !== true) {
      tags.push(t("ngp/palworld/mods/tag/pending"));
    } else if (showClientModFlag(mod)) {
      tags.push(t("ngp/palworld/mods/tag/requires_client_mod"));
    }
    return tags;
  };

  const modHint = (mod: SteamWorkshopItem) => {
    if (mod.isServer === false) {
      return t("ngp/palworld/mods/hint/client_only");
    }
    if (mod.isServer !== true) {
      return t("ngp/palworld/mods/hint/pending");
    }
    if (showClientModFlag(mod)) {
      return t("ngp/palworld/mods/hint/requires_client_mod");
    }
    return undefined;
  };

  return { ...workshop, storeTitle, modTags, isModAddable, modHint, showClientModFlag };
}

const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "basic" } },
  { component: "ConfSection", props: { name: "server" } },
  // WorldOption.sav notice (TR-5175) sits directly above Gameplay — the sav governs gameplay settings.
  { component: "PalworldWorldOptionNotice", props: {} },
  { component: "ConfSection", props: { name: "gameplay" } },
];

export default (ctx: ProfileCtx): Partial<GameProfile> => {
  const ue4ssRaw = ctx.profile?.publicConfigs?.virtual?.ue4ss;
  const ue4ssEnabled = ue4ssRaw === true || ue4ssRaw === "true";

  // Mods are PC-only. The one palworld.ts profile is shared by every Palworld variant
  // (Palworld/PalworldPS5/PalworldXbox/PalworldUwp), but the mod files (UE4SS/pak, from Steam
  // Workshop/Nexus/CurseForge) only load on the Steam PC dedicated server — the console and
  // Microsoft-Store builds (platform PS4/Xbox) run different binaries that can't use them. Gate on
  // the game platform so the whole Modding section and the mod composable never surface there.
  const isPc = ctx.sys?.game?.platform === "PC";

  return {
    menu: createMenu(ue4ssEnabled, isPc),
    configSections,
    consoleCommands,
    ...(isPc ? { useMods } : {}),
  };
};
